// The building plan for "Align sheet to building" (root-04, 2026-09-18):
// one level of the overview (GET /projects/:id/glance) in building feet,
// north up - footprint, rooms and grid lines. A click near a grid
// intersection snaps onto it, so point A / B can be picked exactly.
import React, { useMemo, useRef, useState } from "react";
import { Select, Slider, Tag } from "antd";

const SNAP_PX = 12;
const pts = (poly, Q) => poly.map((p) => Q(p).map((v) => v.toFixed(2)).join(",")).join(" ");

export function levelBounds(manifest, level) {
  let xmin = Infinity; let ymin = Infinity; let xmax = -Infinity; let ymax = -Infinity;
  const eat = (p) => { xmin = Math.min(xmin, p[0]); xmax = Math.max(xmax, p[0]); ymin = Math.min(ymin, p[1]); ymax = Math.max(ymax, p[1]); };
  if (level) { (level.footprint || []).forEach(eat); (level.rooms || []).forEach((r) => (r.polygon || []).forEach(eat)); }
  const g = (manifest && manifest.grid) || {};
  Object.values(g.x || {}).forEach((x) => eat([x, Number.isFinite(ymin) ? ymin : 0]));
  Object.values(g.y || {}).forEach((y) => eat([Number.isFinite(xmin) ? xmin : 0, y]));
  if (!Number.isFinite(xmin)) return null;
  const pad = Math.max(xmax - xmin, ymax - ymin) * 0.06 + 5;
  return { xmin: xmin - pad, ymin: ymin - pad, xmax: xmax + pad, ymax: ymax + pad };
}

// Grid intersections: { label, x, y }.
export function gridIntersections(grid) {
  const out = [];
  for (const [a, x] of Object.entries((grid && grid.x) || {})) {
    for (const [b, y] of Object.entries((grid && grid.y) || {})) out.push({ label: `${a}/${b}`, x, y });
  }
  return out;
}

export default function BuildingSide({ manifest, levelId, onLevel, points, colors, labels, onPick, pickLabel }) {
  const [zoom, setZoom] = useState(1);
  const svg = useRef(null);
  const levels = (manifest && manifest.levels) || [];
  const level = levels.find((l) => l.id === levelId) || levels[0] || null;
  const b = useMemo(() => levelBounds(manifest, level), [manifest, level]);
  const grid = (manifest && manifest.grid) || {};
  const nodes = useMemo(() => gridIntersections(grid), [grid]);
  if (!manifest) return <div style={{ padding: 24, color: "#8D8D8D" }}>Loading the building plan…</div>;
  if (!b) return <div style={{ padding: 24, color: "#8D8D8D" }}>The overview has no plan for this project yet (no rooms or grid).</div>;
  const W = b.xmax - b.xmin; const H = b.ymax - b.ymin;
  // SVG user units = feet, y flipped so north is up.
  const Q = (p) => [p[0] - b.xmin, b.ymax - p[1]];
  const stroke = W / 900;
  const click = (e) => {
    const m = svg.current.getScreenCTM();
    if (!m) return;
    const inv = m.inverse();
    const u = e.clientX * inv.a + e.clientY * inv.c + inv.e; const v = e.clientX * inv.b + e.clientY * inv.d + inv.f;
    const ft = { x: u + b.xmin, y: b.ymax - v };
    const pxPerFt = m.a;
    let best = null; let bd = Infinity;
    for (const n of nodes) { const d = Math.hypot(n.x - ft.x, n.y - ft.y) * pxPerFt; if (d < bd) { bd = d; best = n; } }
    onPick(best && bd <= SNAP_PX ? { x: best.x, y: best.y, label: best.label } : { x: ft.x, y: ft.y });
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", border: "1px solid #EFEFEF", borderRadius: 6, overflow: "hidden" }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 8px", borderBottom: "1px solid #EFEFEF", flexWrap: "wrap" }}>
        <Tag color="#23343E">BUILDING PLAN</Tag>
        <Select size="small" style={{ minWidth: 140 }} value={level && level.id} onChange={onLevel}
          options={levels.map((l) => ({ value: l.id, label: l.name || l.id }))} />
        {pickLabel && <Tag color="orange">Click point {pickLabel} on the plan (snaps to grid intersections)</Tag>}
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#8D8D8D" }}>Zoom</span>
        <Slider style={{ width: 120 }} min={1} max={8} step={0.25} value={zoom} onChange={setZoom} />
      </div>
      <div style={{ flex: 1, overflow: "auto", background: "#fff" }}>
        <svg ref={svg} viewBox={`0 0 ${W} ${H}`} style={{ width: `${zoom * 100}%`, display: "block", cursor: "crosshair" }} onClick={click}>
          {level && level.footprint && <polygon points={pts(level.footprint, Q)} fill="#F3F7F9" stroke="#23343E" strokeWidth={stroke * 2} />}
          {level && (level.rooms || []).map((r) => <polygon key={r.id} points={pts(r.polygon || [], Q)} fill="#E8EEF2" stroke="#9AA9B3" strokeWidth={stroke} />)}
          {Object.entries(grid.x || {}).map(([k, x]) => (
            <g key={`x${k}`}>
              <line x1={x - b.xmin} x2={x - b.xmin} y1={0} y2={H} stroke="#C0392B" strokeWidth={stroke} strokeDasharray={`${stroke * 8} ${stroke * 4}`} />
              <text x={x - b.xmin} y={stroke * 14} fontSize={stroke * 12} textAnchor="middle" fill="#C0392B">{k}</text>
            </g>
          ))}
          {Object.entries(grid.y || {}).map(([k, y]) => (
            <g key={`y${k}`}>
              <line y1={b.ymax - y} y2={b.ymax - y} x1={0} x2={W} stroke="#C0392B" strokeWidth={stroke} strokeDasharray={`${stroke * 8} ${stroke * 4}`} />
              <text x={stroke * 6} y={b.ymax - y - stroke * 3} fontSize={stroke * 12} fill="#C0392B">{k}</text>
            </g>
          ))}
          {points.map((p, i) => p && (
            <g key={i} transform={`translate(${p.x - b.xmin},${b.ymax - p.y})`} style={{ pointerEvents: "none" }}>
              <circle r={stroke * 9} fill="rgba(255,255,255,.4)" stroke={colors[i]} strokeWidth={stroke * 2} />
              <text x={stroke * 11} y={-stroke * 4} fontSize={stroke * 14} fontWeight="700" fill={colors[i]}>{labels[i]}</text>
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}
